import { getCsrfToken } from 'query';

export type LibkeyLibrary = {
  id: number;
  name: string;
  homepage_url: string | null;
  image_url: string | null;
};

export type LibkeySelection = {
  library_id: number | null;
  library_name: string | null;
};

// Carries the server-supplied error message (e.g. `{"error": "Access denied
// on the operation"}`) when the response includes one, so the picker can
// surface it. `serverMessage` is null when the server gave no message.
export class LibkeySaveError extends Error {
  serverMessage: string | null;
  constructor(serverMessage: string | null) {
    super(serverMessage ?? 'save failed');
    this.serverMessage = serverMessage;
  }
}

// Reads the `{ error }` field from a failed response body, returning null when
// the body is absent or unparseable.
const readServerError = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as { error?: string };
    return body.error ?? null;
  } catch {
    return null;
  }
};

export const fetchLibkeyLibraries = async (): Promise<LibkeyLibrary[]> => {
  const response = await fetch('/api/libkey/v1/libraries', {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`LibKey libraries request failed (${response.status})`);
  }
  const body = (await response.json()) as { libraries: LibkeyLibrary[] };
  return body.libraries;
};

export const saveLibkeySelection = async (
  libraryId: number | null
): Promise<LibkeySelection> => {
  const response = await fetch('/api/libkey/v1/profile/libkey_library', {
    method: 'PUT',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ library_id: libraryId }),
  });

  if (response.ok) {
    return (await response.json()) as LibkeySelection;
  }

  throw new LibkeySaveError(await readServerError(response));
};
